import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import { withRouter } from "react-router-dom";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import moment from "moment";

import Breadcrumbs from "components/Common/Breadcrumb";
import { getActivityLogDispatcher } from "store/staff/actions";
import { isEmpty } from "lodash";
import "assets/scss/table.scss";

class DispatcherHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeFilter: "all", // "all" | "Delivered" | "Pending"
      user_id: localStorage.getItem("authUser")
        ? JSON.parse(localStorage.getItem("authUser")).user_id
        : "",
    };
  }

  componentDidMount() {
    const { ongetActivityLogDispatcher } = this.props;
    const routeId = this.props.match?.params?.id;
    const id = routeId || this.state.user_id;

    if (id) {
      ongetActivityLogDispatcher(id);
    } else {
      console.error("ID missing in the request.");
    }
  }

  setFilter = (filter) => {
    this.setState({ activeFilter: filter });
  };

  // Group records by dispatch_date (falls back to submitted_at date)
  groupByDate = (records) => {
    const groups = {};
    records.forEach((rep) => {
      const dateKey = rep.dispatch_date
        ? moment(rep.dispatch_date).format("YYYY-MM-DD")
        : moment(rep.submitted_at).format("YYYY-MM-DD");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(rep);
    });
    return groups;
  };

  formatDateHeading = (dateStr) => {
    const today     = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");
    if (dateStr === today)     return `Today — ${moment(dateStr).format("DD MMM YYYY")}`;
    if (dateStr === yesterday) return `Yesterday — ${moment(dateStr).format("DD MMM YYYY")}`;
    return moment(dateStr).format("DD MMM YYYY");
  };

  getStatusStyle = (status) => {
    switch (status) {
      case "Delivered":
        return { bg: "#E1F5EE", color: "#085041", dot: "#1D9E75", border: "#1D9E75" };
      case "Pending":
        return { bg: "#FFF8E6", color: "#7A4F00", dot: "#F5A623", border: "#F5A623" };
      default:
        return { bg: "#F3F3F1", color: "#666", dot: "#aaa", border: "#aaa" };
    }
  };

  renderDispatchCard = (rep, key) => {
    const st    = this.getStatusStyle(rep.delivery_status);
    const lab   = rep.participant || {};

    return (
      <div key={key} style={styles.timelineItem}>
        <div style={{ ...styles.dot, backgroundColor: st.dot }} />
        <div style={{ ...styles.card, borderLeftColor: st.border }}>

          {/* Top row: status badge + submitted time */}
          <div style={styles.cardTop}>
            <span style={{ ...styles.badge, backgroundColor: st.bg, color: st.color }}>
              {rep.delivery_status === "Delivered" ? "✔ Delivered" : "⏳ Pending"}
            </span>
            <span style={styles.timeText}>
              Dispatched: {rep.dispatch_date
                ? moment(rep.dispatch_date).format("DD MMM YYYY")
                : "—"}
            </span>
          </div>

          {/* Scheme + round */}
          <div style={styles.schemeRow}>
            <span style={styles.schemeName}>{rep.scheme_name || "—"}</span>
            {rep.round_name && (
              <span style={styles.roundBadge}>Round {rep.round_name}</span>
            )}
          </div>

          {/* Lab / Participant info */}
          <div style={styles.labRow}>
            <div style={styles.labIcon}>🏥</div>
            <div>
              <div style={styles.labName}>{lab.name || "—"}</div>
              <div style={styles.labMeta}>
                {[lab.city, lab.district, lab.type].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>

          {/* Fields grid */}
          <div style={styles.fieldsGrid}>
            <div style={styles.field}>
              <div style={styles.fieldLabel}>Tracking ID</div>
              <div style={styles.fieldVal}>{rep.tracking_id || "—"}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.fieldLabel}>Date of Delivery</div>
              <div style={styles.fieldVal}>
                {rep.date_of_delivery
                  ? moment(rep.date_of_delivery).format("DD MMM YYYY")
                  : "—"}
              </div>
            </div>
            <div style={styles.field}>
              <div style={styles.fieldLabel}>Receiver</div>
              <div style={styles.fieldVal}>{rep.receiver || "—"}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.fieldLabel}>Comments</div>
              <div style={styles.fieldVal}>{rep.comments || "—"}</div>
            </div>
          </div>

          {/* Footer: report ID + submitted at */}
          <div style={styles.cardFooter}>
            <span style={styles.reportId}>Report #{rep.report_id}</span>
            <span style={styles.footerTime}>
              Submitted {moment(rep.submitted_at).format("DD MMM YYYY, h:mm A")}
            </span>
          </div>

        </div>
      </div>
    );
  };

  render() {
    const { activitylogDispatcher } = this.props;
    const { activeFilter } = this.state;

    const allRecords = activitylogDispatcher || [];

    const filtered = allRecords.filter((rep) => {
      if (activeFilter === "all")       return true;
      if (activeFilter === "Delivered") return rep.delivery_status === "Delivered";
      if (activeFilter === "Pending")   return rep.delivery_status === "Pending";
      return true;
    });

    const totalAll       = allRecords.length;
    const totalDelivered = allRecords.filter(r => r.delivery_status === "Delivered").length;
    const totalPending   = allRecords.filter(r => r.delivery_status === "Pending").length;

    const grouped     = this.groupByDate(filtered);
    const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

    const filterBtnStyle = (type) => ({
      ...styles.filterBtn,
      ...(activeFilter === type ? styles.filterActive : {}),
    });

    return (
      <React.Fragment>
        <div className="page-content">
          <MetaTags>
            <title>Dispatcher History | NHS NEQAS</title>
          </MetaTags>
          <Container fluid>
            <Breadcrumbs title="Staff" breadcrumbItem="Dispatcher History" />

            <Row>
              <Col lg="8" className="offset-lg-2">

                {/* Stat cards */}
                <Row className="mb-3">
                  <Col xs="4">
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Total dispatches</div>
                      <div style={{ ...styles.statVal, color: "#3d3d3a" }}>{totalAll}</div>
                    </div>
                  </Col>
                  <Col xs="4">
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Delivered</div>
                      <div style={{ ...styles.statVal, color: "#0F6E56" }}>{totalDelivered}</div>
                    </div>
                  </Col>
                  <Col xs="4">
                    <div style={styles.statCard}>
                      <div style={styles.statLabel}>Pending</div>
                      <div style={{ ...styles.statVal, color: "#B07D00" }}>{totalPending}</div>
                    </div>
                  </Col>
                </Row>

                {/* Filter buttons */}
                <div style={styles.filterRow}>
                  <button style={filterBtnStyle("all")}       onClick={() => this.setFilter("all")}>All</button>
                  <button style={filterBtnStyle("Delivered")} onClick={() => this.setFilter("Delivered")}>✔ Delivered</button>
                  <button style={filterBtnStyle("Pending")}   onClick={() => this.setFilter("Pending")}>⏳ Pending</button>
                </div>

                <Card>
                  <CardBody>
                    {!isEmpty(filtered) ? (
                      sortedDates.map((dateKey) => (
                        <div key={dateKey}>
                          <div style={styles.dateDivider}>
                            {this.formatDateHeading(dateKey)}
                          </div>
                          <div style={styles.timeline}>
                            {grouped[dateKey].map((rep, idx) =>
                              this.renderDispatchCard(rep, `${dateKey}-${idx}`)
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <Row>
                        <Col lg="12">
                          <div className="mb-5 mt-3">
                            <h5 className="text-uppercase text-muted">
                              No dispatch records found.
                            </h5>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </CardBody>
                </Card>

              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

const styles = {
  statCard: {
    backgroundColor: "#f8f8f6",
    borderRadius: "8px",
    padding: "12px 14px",
  },
  statLabel: {
    fontSize: "12px",
    color: "#888",
    marginBottom: "4px",
  },
  statVal: {
    fontSize: "22px",
    fontWeight: "500",
  },
  filterRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  filterBtn: {
    padding: "5px 14px",
    border: "1px solid #ddd",
    borderRadius: "20px",
    fontSize: "12px",
    color: "#666",
    background: "#fff",
    cursor: "pointer",
  },
  filterActive: {
    border: "1px solid #888",
    background: "#f0f0ee",
    color: "#2c2c2a",
    fontWeight: "500",
  },
  dateDivider: {
    fontSize: "11px",
    fontWeight: "500",
    color: "#aaa",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    marginBottom: "10px",
    marginTop: "8px",
  },
  timeline: {
    position: "relative",
    paddingLeft: "28px",
    borderLeft: "1px solid #e8e8e6",
    marginLeft: "6px",
    marginBottom: "8px",
  },
  timelineItem: {
    position: "relative",
    marginBottom: "10px",
  },
  dot: {
    position: "absolute",
    left: "-34px",
    top: "14px",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    border: "2px solid #fff",
  },
  card: {
    backgroundColor: "#fff",
    border: "1px solid #ebebeb",
    borderLeft: "3px solid transparent",
    borderRadius: "8px",
    padding: "12px 14px",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  badge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "500",
  },
  timeText: {
    fontSize: "11px",
    color: "#aaa",
  },
  schemeRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  schemeName: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#2c2c2a",
  },
  roundBadge: {
    fontSize: "10px",
    padding: "2px 8px",
    borderRadius: "10px",
    backgroundColor: "#f0f0ee",
    color: "#666",
    fontWeight: "500",
  },
  labRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "10px",
    paddingBottom: "10px",
    borderBottom: "1px solid #f0f0ee",
  },
  labIcon: {
    fontSize: "16px",
    lineHeight: 1.2,
    flexShrink: 0,
  },
  labName: {
    fontSize: "12px",
    fontWeight: "500",
    color: "#2c2c2a",
  },
  labMeta: {
    fontSize: "11px",
    color: "#888",
    marginTop: "1px",
  },
  fieldsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px 20px",
    marginBottom: "10px",
  },
  field: {
    fontSize: "12px",
  },
  fieldLabel: {
    color: "#888",
    marginBottom: "2px",
  },
  fieldVal: {
    color: "#2c2c2a",
    fontWeight: "500",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "8px",
    borderTop: "1px solid #f0f0ee",
  },
  reportId: {
    fontSize: "10px",
    color: "#bbb",
    fontFamily: "monospace",
  },
  footerTime: {
    fontSize: "10px",
    color: "#bbb",
  },
};

DispatcherHistory.propTypes = {
  match:                      PropTypes.object,
  activitylogDispatcher:      PropTypes.array,
  ongetActivityLogDispatcher: PropTypes.func,
  location:                   PropTypes.object,
  error:                      PropTypes.any,
};

const mapStateToProps = state => ({
  activitylogDispatcher: state.staff.activitylogDispatcher,
});

const mapDispatchToProps = dispatch => ({
  ongetActivityLogDispatcher: id => dispatch(getActivityLogDispatcher(id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(DispatcherHistory));